import { useState, useEffect, ReactNode } from 'react';
import { RestaurantDashboard } from './components/dashboard/RestaurantDashboard';
import { CustomersDashboard } from './components/dashboard/CustomersDashboard';
import { settingsService } from './services/settings.service';
import { OrdersPage } from './components/orders/OrdersPage';
import { OrderDetailsPage } from './components/orders/OrderDetailsPage';
import { CustomersPage } from './components/customers/CustomersPage';
import { CustomerDetailsPage } from './components/customers/CustomerDetailsPage';
import { MembersPage } from './components/customers/MembersPage';
import { TransactionsPage } from './components/customers/TransactionsPage';
import { AnalyticsPage } from './components/analytics/AnalyticsPage';
import { ReviewsPage } from './components/reviews/ReviewsPage';
import { SettingsPage } from './components/settings/SettingsPage';
import { CategoryProductsPage } from './components/settings/CategoryProductsPage';
import { MenuTypesPage } from './components/menus/MenuTypesPage';
import { MenusPage } from './components/menus/MenusPage';
import { MenuItemsPage } from './components/menus/MenuItemsPage';
import { TablesPage } from './components/tables/TablesPage';
import { CartPage } from './components/cart/CartPage';
import { NotificationsPage } from './components/notifications/NotificationsPage';
import { PromotionsPage } from './components/promotions/PromotionsPage';
import { ProfilePage } from './components/profile/ProfilePage';
import { AboutPage } from './components/about/AboutPage';
import { TasksPage } from './components/tasks/TasksPage';
import { HelpPage } from './components/help/HelpPage';
import { LoginPage } from './components/auth/LoginPage';
import { UsersManagementPage } from './components/users/UsersManagementPage';
import { RbacManagementPage } from './components/rbac/RbacManagementPage';
import { TenantsManagementPage } from './components/tenants/TenantsManagementPage';
import { ReportsPage } from './components/reports/ReportsPage';
import { AddCustomerModal } from './components/customers/AddCustomerModal';
import { LogoutConfirmationModal } from './components/shared/LogoutConfirmationModal';
import { SuccessModal } from './components/shared/SuccessModal';
import { ThemeProvider } from './contexts/ThemeContext';
import { RestaurantProvider } from './contexts/RestaurantContext';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { hasEffectivePermission } from './utils/rbac';
import { Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import {MessageBatchesPage}  from './components/messaging/MessageBatchesPage';
import {MessageComposer}  from './components/messaging/MessageComposer';
import {MessagingProvidersPage}  from './components/messaging/MessagingProvidersPage';
import {MessageTemplatesPage}  from './components/messaging/MessageTemplatesPage';

type PageType = 'dashboard' | 'orders' | 'orderDetails' | 'customers' | 'customers-general' | 'customers-members' | 'customerDetails' | 'members' | 'analytics' | 'reports' | 'reviews' | 'settings' | 'categoryProducts' | 'menuTypes' | 'menus' | 'menuItems' | 'tables' | 'cart' | 'notifications' | 'promotions' | 'profile' | 'about' | 'tasks' | 'help' | 'users' | 'rbac' | 'tenants' | 'transactions' | 'message-templates' | 'message-batches' | 'create-batch' | 'providers';
type SettingsTabType = 'general' | 'menuTypes' | 'notifications' | 'security' | 'appearance' | 'sidebar' | 'tables';

type PageWithSidebarProps = {
  activePage: PageType;
  onNavigate: (page: string) => void;
  children: ReactNode;
};

function PageWithSidebar({ activePage, onNavigate, children }: PageWithSidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePage={activePage}
        onNavigate={onNavigate}
      />
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

function AppContent() {
  const { logout, user } = useAuth();

  const [isRestaurant, setIsRestaurant] = useState<boolean | null>(null);

  useEffect(() => {
    settingsService.getTenantSettings().then((r) => {
      setIsRestaurant(r.data?.isRestaurant ?? true);
    }).catch(() => setIsRestaurant(true));
  }, [user?.tenantId]);

  const [currentPage, setCurrentPage] = useState<PageType>(() => {
    // Restore current page from localStorage on mount
    const savedPage = localStorage.getItem('currentPage');
    return (savedPage as PageType) || 'dashboard';
  });
  
  const [selectedOrderId, setSelectedOrderId] = useState<string>(() => {
    return localStorage.getItem('selectedOrderId') || '';
  });

  const [orderDetailsSourcePage, setOrderDetailsSourcePage] = useState<PageType>(() => {
    const savedSource = localStorage.getItem('orderDetailsSourcePage');
    return (savedSource as PageType) || 'orders';
  });
  
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => {
    return localStorage.getItem('selectedCustomerId') || '';
  });
  
  const [selectedMenuTypeId, setSelectedMenuTypeId] = useState<string>(() => {
    return localStorage.getItem('selectedMenuTypeId') || '';
  });
  
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(() => {
    return localStorage.getItem('selectedCategoryId') || '';
  });
  
  const [customerType, setCustomerType] = useState<'all' | 'general' | 'members'>(() => {
    const saved = localStorage.getItem('customerType');
    return (saved as 'all' | 'general' | 'members') || 'all';
  });
  
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [isLogoutConfirmationModalOpen, setIsLogoutConfirmationModalOpen] = useState(false);
  const [successModalData, setSuccessModalData] = useState<{ title: string; message: string } | null>(null);

  // Check if user has permission to access a page
  const hasPageAccess = (page: string): boolean => {
    if (!user) return false;
    
    // Map pages to permissions
    const pagePermissionMap: Record<string, string> = {
      'dashboard': 'dashboard',
      'orders': 'orders',
      'orderDetails': 'view_order_details',
      'menuTypes': 'menus',
      'menus': 'menus',
      'menuItems': 'menus',
      'tables': 'tables',
      'customers': 'customers',
      'customers-general': 'customers',
      'customers-members': 'customers',
      'customerDetails': 'customers',
      'members': 'customers',
      'transactions': 'transactions',
      'reviews': 'reviews',
      'analytics': 'analytics',
      'reports': 'reports',
      'settings': 'settings',
      'categoryProducts': 'settings',
      'users': 'users',
      'rbac': 'roles',
      'tenants': 'tenants_platform',
      'profile': 'dashboard', // Everyone can access profile
      'about': 'dashboard', // Everyone can access about
      'tasks': 'dashboard', // Everyone can access tasks
      'help': 'dashboard', // Everyone can access help
      'cart': 'menus', // Cart requires menu access
      'notifications': 'dashboard', // Everyone can access notifications
      'promotions': 'menus', // Promotions require menu access
      'message-templates': 'messaging',
      'message-batches': 'messaging',
      'create-batch': 'messaging',
      'providers': 'messaging',
    };

    const requiredPermission = pagePermissionMap[page];
    if (!requiredPermission) return true; // Allow if no specific permission needed
    
    return hasEffectivePermission(user, requiredPermission);
  };

  const handleNavigation = (page: string) => {
    // Handle logout
    if (page === 'logout') {
      setIsLogoutConfirmationModalOpen(true);
      return;
    }

    // Handle menu-items-{categoryId} navigation pattern
    if (page.startsWith('menu-items-')) {
      const categoryId = page.replace('menu-items-', '');
      if (categoryId) {
        handleCategoryClick(categoryId);
        return;
      }
    }

    // Handle category-products-{categoryId} navigation pattern
    if (page.startsWith('category-products-')) {
      const categoryId = page.replace('category-products-', '');
      if (categoryId) {
        setSelectedCategoryId(categoryId);
        localStorage.setItem('selectedCategoryId', categoryId);
      }
      setCurrentPage('categoryProducts');
      return;
    }

    // Handle customer-details-{customerId} navigation pattern
    if (page.startsWith('customer-details-')) {
      const customerId = page.replace('customer-details-', '');
      if (customerId) {
        setSelectedCustomerId(customerId);
        setCurrentPage('customerDetails');
      }
      return;
    }

    // Handle settings-menuTypes-{menuTypeId} navigation pattern
    if (page.startsWith('settings-menuTypes-')) {
      const focusedMenuTypeId = page.replace('settings-menuTypes-', '');
      localStorage.setItem('settingsActiveTab', 'menuTypes');
      if (focusedMenuTypeId) {
        localStorage.setItem('settingsFocusMenuTypeId', focusedMenuTypeId);
      }
      setCurrentPage('settings');
      return;
    }

    // Handle settings-{tab} navigation pattern
    if (page.startsWith('settings-')) {
      const requestedTab = page.replace('settings-', '') as SettingsTabType;
      const validTabs: SettingsTabType[] = ['general', 'menuTypes', 'notifications', 'security', 'appearance', 'sidebar', 'tables'];

      if (validTabs.includes(requestedTab)) {
        localStorage.setItem('settingsActiveTab', requestedTab);
      }

      if (requestedTab !== 'menuTypes') {
        localStorage.removeItem('settingsFocusMenuTypeId');
      }

      setCurrentPage('settings');
      return;
    }

    // Check if user has permission to access the page
    if (!hasPageAccess(page)) {
      toast.error('Access Denied', {
        description: 'You do not have permission to access this page.',
      });
      return;
    }

    // Handle customers navigation
    if (page === 'customers-general') {
      setCustomerType('general');
      setCurrentPage('customers');
      return;
    }

    if (page === 'customers-members') {
      setCurrentPage('members');
      return;
    }

    // Reset customerType when navigating to the plain customers page
    if (page === 'customers') {
      setCustomerType('all');
      setCurrentPage('customers');
      return;
    }

    // Reset menuTypeId when navigating to menuTypes
    if (page === 'menuTypes') {
      setSelectedMenuTypeId('');
      setCurrentPage('menuTypes');
      return;
    }

    // When navigating to menus, read menuTypeId from localStorage
    if (page === 'menus') {
      const storedMenuTypeId = localStorage.getItem('selectedMenuTypeId') || '';
      if (storedMenuTypeId) {
        setSelectedMenuTypeId(storedMenuTypeId);
      }
      setCurrentPage('menus');
      return;
    }

    setCurrentPage(page as PageType);
  };

  const handleOrderClick = (orderId: string) => {
    if (!hasPageAccess('orderDetails')) {
      toast.error('Access Denied', {
        description: 'You do not have permission to view order details.',
      });
      return;
    }

    const sourcePage = currentPage === 'orderDetails' ? orderDetailsSourcePage : currentPage;
    setOrderDetailsSourcePage(sourcePage);
    setSelectedOrderId(orderId);
    setCurrentPage('orderDetails');
  };

  const handleCustomerClick = (customerId: string) => {
    if (!hasPageAccess('customers')) {
      toast.error('Access Denied', {
        description: 'You do not have permission to view customer details.',
      });
      return;
    }
    setSelectedCustomerId(customerId);
    setCurrentPage('customerDetails');
  };

  const handleCategoryClick = (categoryId: string) => {
    if (!hasPageAccess('menus')) {
      toast.error('Access Denied', {
        description: 'You do not have permission to view menu items.',
      });
      return;
    }
    setSelectedCategoryId(categoryId);
    setCurrentPage('menuItems');
  };

  const handleMenuTypeClick = (menuTypeId: string) => {
    if (!hasPageAccess('menus')) {
      toast.error('Access Denied', {
        description: 'You do not have permission to view menu categories.',
      });
      return;
    }
    setSelectedMenuTypeId(menuTypeId);
    setCurrentPage('menus');
  };

  const handleBackToOrders = () => {
    if (orderDetailsSourcePage === 'orderDetails') {
      setCurrentPage('orders');
      return;
    }
    setCurrentPage(orderDetailsSourcePage || 'orders');
  };

  const getOrderDetailsBackLabel = () => {
    switch (orderDetailsSourcePage) {
      case 'dashboard':
        return 'Dashboard';
      case 'notifications':
        return 'Notifications';
      case 'orders':
      default:
        return 'Orders';
    }
  };

  const handleBackToCustomers = () => {
    setCurrentPage('customers');
  };

  const handleEditCustomer = (_customerId: string) => {
    // Navigation handled by CustomersPage
  };

  const handleAddCustomer = () => {
    setIsAddCustomerModalOpen(true);
  };

  const handleCloseAddCustomerModal = () => {
    setIsAddCustomerModalOpen(false);
  };

  const handleSaveNewCustomer = (data: any) => {
    // Generate a new customer ID
    const newId = `#CM${(11).toString().padStart(4, '0')}`; // Starting from CM0011
    
    // Build success message
    let message = `Customer ${data.name} (${newId}) added successfully!`;
    
    if (data.addedToMembersList) {
      message += '\n\n✓ Customer has been added to the Members List and will receive regular notifications about promotions and special offers.';
    } else if (data.receiveNotifications) {
      message += '\n\n✓ Customer will receive regular notifications.';
    }
    
    // In a real app, this would make an API call to save the customer
    toast.success(message);
    setIsAddCustomerModalOpen(false);
    setSuccessModalData({ title: 'Customer Added', message: message });
  };

  const handleLogout = () => {
    // Clear persisted navigation state
    localStorage.removeItem('currentPage');
    localStorage.removeItem('selectedOrderId');
    localStorage.removeItem('orderDetailsSourcePage');
    localStorage.removeItem('selectedCustomerId');
    localStorage.removeItem('selectedMenuTypeId');
    localStorage.removeItem('selectedCategoryId');
    localStorage.removeItem('customerType');
    localStorage.removeItem('sidebar_customersExpanded');
    
    logout();
    toast.success('Logged out successfully');
    setIsLogoutConfirmationModalOpen(false);
  };

  const handleCloseLogoutConfirmationModal = () => {
    setIsLogoutConfirmationModalOpen(false);
  };

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('currentPage', currentPage);
  }, [currentPage]);

  useEffect(() => {
    localStorage.setItem('selectedOrderId', selectedOrderId);
  }, [selectedOrderId]);

  useEffect(() => {
    localStorage.setItem('orderDetailsSourcePage', orderDetailsSourcePage);
  }, [orderDetailsSourcePage]);

  useEffect(() => {
    localStorage.setItem('selectedCustomerId', selectedCustomerId);
  }, [selectedCustomerId]);

  useEffect(() => {
    localStorage.setItem('selectedMenuTypeId', selectedMenuTypeId);
  }, [selectedMenuTypeId]);

  useEffect(() => {
    localStorage.setItem('selectedCategoryId', selectedCategoryId);
  }, [selectedCategoryId]);

  useEffect(() => {
    localStorage.setItem('customerType', customerType);
  }, [customerType]);

  if (currentPage === 'customerDetails') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <CustomerDetailsPage 
              customerId={selectedCustomerId}
              onNavigate={handleNavigation}
              onBack={handleBackToCustomers}
              onEdit={handleEditCustomer}
            />
            {isAddCustomerModalOpen && (
              <AddCustomerModal
                onClose={handleCloseAddCustomerModal}
                onSave={handleSaveNewCustomer}
              />
            )}
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            {successModalData && (
              <SuccessModal
                title={successModalData.title}
                message={successModalData.message}
                onClose={() => setSuccessModalData(null)}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'orderDetails') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <OrderDetailsPage 
              orderId={selectedOrderId}
              onNavigate={handleNavigation}
              onBack={handleBackToOrders}
              backLabel={getOrderDetailsBackLabel()}
              onOrderChange={handleOrderClick}
            />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'orders') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <OrdersPage onNavigate={handleNavigation} onOrderClick={handleOrderClick} />
            {isAddCustomerModalOpen && (
              <AddCustomerModal
                onClose={handleCloseAddCustomerModal}
                onSave={handleSaveNewCustomer}
              />
            )}
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'customers') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <CustomersPage onNavigate={handleNavigation} onAddCustomer={handleAddCustomer} customerType={customerType} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'members') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <MembersPage onNavigate={handleNavigation} />
            {isAddCustomerModalOpen && (
              <AddCustomerModal
                onClose={handleCloseAddCustomerModal}
                onSave={handleSaveNewCustomer}
              />
            )}
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'transactions') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <TransactionsPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'analytics') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <AnalyticsPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'reports') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <ReportsPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'tenants') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <TenantsManagementPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'reviews') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <ReviewsPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'settings') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <SettingsPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'categoryProducts') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <CategoryProductsPage onNavigate={handleNavigation} categoryId={selectedCategoryId} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'menuTypes') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <MenuTypesPage onNavigate={handleNavigation} onMenuTypeClick={handleMenuTypeClick} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'tables') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <TablesPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'menus') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <MenusPage onNavigate={handleNavigation} onCategoryClick={handleCategoryClick} menuTypeId={selectedMenuTypeId} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'menuItems') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <MenuItemsPage onNavigate={handleNavigation} categoryId={selectedCategoryId} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'cart') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <CartPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'notifications') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <NotificationsPage onNavigate={handleNavigation} onOrderClick={handleOrderClick} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'promotions') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <PromotionsPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'profile') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <ProfilePage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'about') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <AboutPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'tasks') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <TasksPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'help') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <HelpPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'users') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <UsersManagementPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'message-batches') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <PageWithSidebar activePage="message-batches" onNavigate={handleNavigation}>
              <MessageBatchesPage
                onNavigate={handleNavigation}
                tenantId={user?.tenantId}
              />
            </PageWithSidebar>
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'message-templates') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <PageWithSidebar activePage="message-templates" onNavigate={handleNavigation}>
              <MessageTemplatesPage
                onNavigate={handleNavigation}
                tenantId={user?.tenantId}
              />
            </PageWithSidebar>
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'create-batch') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <PageWithSidebar activePage="create-batch" onNavigate={handleNavigation}>
              <MessageComposer
                onNavigate={handleNavigation}
                tenantId={user?.tenantId}
              />
            </PageWithSidebar>
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  if (currentPage === 'providers') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <PageWithSidebar activePage="providers" onNavigate={handleNavigation}>
              <MessagingProvidersPage
                onNavigate={handleNavigation}
                tenantId={user?.tenantId}
              />
            </PageWithSidebar>
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }

  
  if (currentPage === 'rbac') {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <RbacManagementPage onNavigate={handleNavigation} />
            {isLogoutConfirmationModalOpen && (
              <LogoutConfirmationModal
                onClose={handleCloseLogoutConfirmationModal}
                onLogout={handleLogout}
              />
            )}
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }
  

  return (
    <ThemeProvider>
      <RestaurantProvider>
        <CartProvider>
          {isRestaurant === false ? (
            <CustomersDashboard onNavigate={handleNavigation} />
          ) : (
            <RestaurantDashboard
              onNavigate={handleNavigation}
              onOrderClick={handleOrderClick}
              onCustomerClick={handleCustomerClick}
              onCategoryClick={handleCategoryClick}
            />
          )}
          {isAddCustomerModalOpen && (
            <AddCustomerModal
              onClose={handleCloseAddCustomerModal}
              onSave={handleSaveNewCustomer}
            />
          )}
          {isLogoutConfirmationModalOpen && (
            <LogoutConfirmationModal
              onClose={handleCloseLogoutConfirmationModal}
              onLogout={handleLogout}
            />
          )}
          {successModalData && (
            <SuccessModal
              title={successModalData.title}
              message={successModalData.message}
              onClose={() => setSuccessModalData(null)}
            />
          )}
          <Toaster />
        </CartProvider>
      </RestaurantProvider>
    </ThemeProvider>
  );
}

// Authentication gate component
function AuthGate() {
  const { isAuthenticated, user, activeTenantId } = useAuth();
  const [, setRefreshKey] = useState(0);
  
  // Force refresh on login
  const handleLoginSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };
  
  if (!isAuthenticated) {
    return (
      <ThemeProvider>
        <RestaurantProvider>
          <CartProvider>
            <LoginPage onLoginSuccess={handleLoginSuccess} />
            <Toaster />
          </CartProvider>
        </RestaurantProvider>
      </ThemeProvider>
    );
  }
  
  // When activeTenantId changes (tenant switch), React unmounts and remounts AppContent
  // with a new key. This gives a clean slate to all hooks and contexts without a browser
  // reload, eliminating race conditions between async re-initialization tasks.
  const contentKey = activeTenantId || user?.tenantId || 'default';
  return <AppContent key={contentKey} />;
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}